import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Search, BarChart3, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import BackOrHomeButton from '@/components/BackOrHomeButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /glossary/cef-strategies
 *
 * Companion page to the main Glossary that lists every distinct
 * Morningstar CEF strategy currently represented in DiviDrip's
 * curated CEF data. Each card shows:
 *   - Strategy name
 *   - Count of CEFs in that strategy (DiviDrip's coverage)
 *   - Full plain-English definition (collapsible)
 *   - Quick-jump button → Stock Screener pre-filtered to just that strategy
 *
 * Backend source: GET /api/cef-strategies
 *
 * Why this page exists: with 24+ Morningstar strategy classifications
 * (Municipal, Senior Loans, Covered Call, MLP, Real Estate, etc.) the
 * main Glossary CEF entry can't fit them all without becoming a wall
 * of text. This page is the deep-dive reference.
 */
export default function CefStrategies() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  // Round P136 (Feb 2026) — Leverage toggle. When ON, structurally-
  // leveraged CEFs (PDI, PDO, UTG, DSL, etc. — curated set in
  // backend/data/leveraged_cefs.py) get an amber highlight in each
  // strategy card's ticker list, and every card header shows a small
  // "⚡ N leveraged" badge. Doesn't hide anything — it's a lens, not a
  // filter, so users can still browse the whole taxonomy while
  // seeing which names use structural leverage.
  const [showLeverage, setShowLeverage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/cef-strategies`)
      .then((res) => {
        if (cancelled) return;
        setStrategies(res.data?.strategies || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || 'Failed to load strategies');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return strategies;
    return strategies.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      (s.definition || '').toLowerCase().includes(q) ||
      (s.tickers || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [strategies, search]);

  const totalCefs = strategies.reduce((sum, s) => sum + (s.count || 0), 0);
  const totalLeveraged = strategies.reduce((sum, s) => sum + (s.leveraged_count || 0), 0);

  const toggle = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(strategies.map((s) => s.name)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="min-h-screen min-h-[100svh] bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-0 space-y-6" data-testid="cef-strategies-page">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BackOrHomeButton testid="cef-strategies-back-btn" />
        </div>

        <header className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="cef-strategies-title">
            CEF Strategies — Full Reference
          </h1>
          <p className="text-base text-muted-foreground">
            Morningstar classifies every Closed-End Fund into a strategy bucket based on what it
            actually holds — Municipal bonds, Senior Loans, Covered Calls, MLPs, Sector Equity, etc.
            Different strategies behave differently (interest-rate sensitivity, leverage, tax
            treatment), so understanding the bucket matters more than just chasing the headline yield.
          </p>
          {!loading && !error && (
            <p className="text-sm text-muted-foreground">
              <span className="font-mono font-semibold text-foreground">{strategies.length}</span> strategies tracked
              {' · '}
              <span className="font-mono font-semibold text-foreground">{totalCefs}</span> curated CEFs across DiviDrip
            </p>
          )}
        </header>

        {!loading && !error && (
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-y border-border py-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search strategies, descriptions, or ticker symbols…"
                className="pl-9"
                data-testid="cef-strategies-search"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={showLeverage ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowLeverage((v) => !v)}
                data-testid="cef-strategies-leverage-toggle"
                aria-pressed={showLeverage}
                className={showLeverage
                  ? 'gap-1.5 bg-amber-500 hover:bg-amber-500/90 text-black border-amber-500'
                  : 'gap-1.5 border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10'}
                title={
                  showLeverage
                    ? 'Hide the leverage highlight'
                    : `Highlight ${totalLeveraged} structurally-leveraged CEFs (PDI, PDO, UTG, DSL, etc.)`
                }
              >
                <Zap className="h-3.5 w-3.5" />
                {showLeverage ? 'Leverage on' : 'Leverage'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={expandAll}
                data-testid="cef-strategies-expand-all"
              >
                Expand all
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAll}
                data-testid="cef-strategies-collapse-all"
              >
                Collapse all
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && showLeverage && (
          <p
            className="text-xs text-amber-700 dark:text-amber-400 -mt-1"
            data-testid="cef-strategies-leverage-hint"
          >
            <Zap className="inline h-3 w-3 mb-0.5 mr-1" />
            <span className="font-semibold">{totalLeveraged}</span> structurally-leveraged CEFs highlighted
            across all buckets. Leverage isn&apos;t a Morningstar strategy — it&apos;s a
            characteristic layered on top (typically 25-45% effective leverage).
          </p>
        )}

        {loading && (
          <div className="text-center py-12 text-muted-foreground" data-testid="cef-strategies-loading">
            Loading strategies…
          </div>
        )}

        {error && (
          <div className="text-center py-12 text-rose-500" data-testid="cef-strategies-error">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="cef-strategies-empty">
            No strategies match &ldquo;{search}&rdquo;.
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ul className="space-y-3" data-testid="cef-strategies-list">
            {filtered.map((s) => {
              const isOpen = expanded.has(s.name);
              const shortName = s.name.replace(/^Morningstar US CEF /, '');
              const slug = encodeURIComponent(s.name);
              return (
                <li
                  key={s.name}
                  className="rounded-lg border border-border bg-card overflow-hidden"
                  data-testid={`cef-strategy-card-${shortName.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(s.name)}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
                    data-testid={`cef-strategy-toggle-${shortName.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-base sm:text-lg font-semibold truncate">{shortName}</span>
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded shrink-0">
                        {s.count} {s.count === 1 ? 'CEF' : 'CEFs'}
                      </span>
                      {showLeverage && (s.leveraged_count || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 px-1.5 py-0.5 rounded shrink-0"
                          data-testid={`cef-strategy-leveraged-badge-${shortName.replace(/\s+/g, '-').toLowerCase()}`}
                          title={`${s.leveraged_count} of ${s.count} funds in this bucket use structural leverage`}
                        >
                          <Zap className="h-3 w-3" />
                          {s.leveraged_count}
                        </span>
                      )}
                    </div>
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border bg-muted/20">
                      {s.definition ? (
                        <div className="text-sm text-foreground/90 whitespace-pre-line leading-relaxed">
                          {s.definition}
                        </div>
                      ) : (
                        <div className="text-sm italic text-muted-foreground">
                          Definition pending. Open a CEF in this strategy to see its full description.
                        </div>
                      )}
                      {s.tickers && s.tickers.length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-semibold">Tickers in DiviDrip ({s.tickers.length}):</span>{' '}
                          {showLeverage && (s.leveraged_tickers || []).length > 0 ? (
                            <span className="font-mono">
                              {s.tickers.map((t, i) => {
                                const isLev = (s.leveraged_tickers || []).includes(t);
                                return (
                                  <span key={t}>
                                    {i > 0 && ', '}
                                    {isLev ? (
                                      <span
                                        className="font-semibold text-amber-700 dark:text-amber-400"
                                        title="Structurally leveraged (curated in DiviDrip)"
                                        data-testid={`cef-strategy-lev-ticker-${t}`}
                                      >
                                        {t}
                                      </span>
                                    ) : (
                                      <span>{t}</span>
                                    )}
                                  </span>
                                );
                              })}
                            </span>
                          ) : (
                            <span className="font-mono">{s.tickers.join(', ')}</span>
                          )}
                        </div>
                      )}
                      <div className="pt-1">
                        <Link
                          to={`/screener?cef_strategy=${slug}`}
                          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                          data-testid={`cef-strategy-screener-link-${shortName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <BarChart3 className="h-4 w-4" />
                          Open in Stock Screener →
                        </Link>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <SiteDisclaimer />
    </div>
  );
}
